import { message, notification } from 'ant-design-vue';
import { onMounted, onUnmounted, ref, Ref } from 'vue';

/**
 * @param targetElRef 要全屏的元素
 * @param screenFullscreen 是否显示器全屏，为false时仅窗口全屏
 */
export function useFullscreen(targetElRef: Ref<HTMLElement | undefined>, screenFullscreen = false) {
  return screenFullscreen ? useScreenFullscreen(targetElRef) : useWindowFullscreen(targetElRef);
}

export function useScreenFullscreen(targetElRef: Ref<HTMLElement | undefined>) {
  const isFullscreen = ref(false);

  const onFullscreenChange = () => {
    isFullscreen.value = Boolean(document.fullscreenElement);
  };
  onMounted(() => {
    document.addEventListener('fullscreenchange', onFullscreenChange);
  });
  onUnmounted(() => {
    document.removeEventListener('fullscreenchange', onFullscreenChange);
  });

  const toggleFullscreen = async () => {
    if (isFullscreen.value) {
      await document.exitFullscreen();

      // 缓存机制会导致设置无效，需清除所有实例
      notification.destroy();
      notification.config({
        getContainer: () => document.body
      });
      message.config({
        getContainer: () => document.body
      });
    } else {
      await targetElRef.value?.requestFullscreen();

      // 缓存机制会导致设置无效，需清除所有实例
      notification.destroy();
      notification.config({
        getContainer: () => targetElRef.value || document.body
      });
      message.config({
        getContainer: () => targetElRef.value || document.body
      });
    }
  };
  return {
    isFullscreen,
    toggleFullscreen
  };
}

export function useWindowFullscreen(targetElRef: Ref<HTMLElement | undefined>) {
  const isFullscreen = ref(false);
  const originPosition = targetElRef.value?.style.position;
  const originLeft = targetElRef.value?.style.left;
  const originRight = targetElRef.value?.style.right;
  const originTop = targetElRef.value?.style.top;
  const originBottom = targetElRef.value?.style.bottom;
  const originZindex = targetElRef.value?.style.zIndex;
  const originOverflowY = targetElRef.value?.style.overflowY;

  const keyUpHandler = async (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      if (isFullscreen.value) {
        await toggleFullscreen();
      }
    }
  };
  onMounted(() => {
    window.addEventListener('keyup', keyUpHandler);
  });
  onUnmounted(() => {
    window.removeEventListener('keyup', keyUpHandler);
  });

  const toggleFullscreen = async () => {
    if (!targetElRef.value) {
      return;
    }
    if (isFullscreen.value) {
      targetElRef.value!.style.position = originPosition || 'static';
      targetElRef.value!.style.left = originLeft || 'auto';
      targetElRef.value!.style.right = originRight || 'auto';
      targetElRef.value!.style.top = originTop || 'auto';
      targetElRef.value!.style.bottom = originBottom || 'auto';
      targetElRef.value!.style.zIndex = originZindex || 'auto';
      targetElRef.value!.style.overflowY = originOverflowY || 'auto';

      isFullscreen.value = false;
    } else {
      targetElRef.value!.style.position = 'fixed';
      targetElRef.value!.style.left = '0';
      targetElRef.value!.style.right = '0';
      targetElRef.value!.style.top = '0';
      targetElRef.value!.style.bottom = '0';
      // FIXME: 层级可能影响其他组件
      targetElRef.value!.style.zIndex = '10';
      targetElRef.value!.style.overflowY = 'auto';

      isFullscreen.value = true;
    }
  };
  return {
    isFullscreen,
    toggleFullscreen
  };
}
